import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import WeekTabs from './components/WeekTabs';
import DayView from './components/DayView';
import TaskComposer from './components/TaskComposer';
import SuggestedPlan from './components/SuggestedPlan';
import Header from './components/Header';
import AboutUsPage from './components/AboutUsPage';
import ContactPage from './components/ContactPage';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import LandingPage from './components/LandingPage';
import ProfilePage from './components/ProfilePage';
import SettingsPage from './components/SettingsPage';
import ActivityPage from './components/ActivityPage';
import HelpPage from './components/HelpPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TuduLoader from './components/TuduLoader';
import AppNav from './components/AppNav';
import SharedTabView from './components/SharedTabView';
import RoutinesScreen from './components/RoutinesScreen';
import DealsScreen from './components/DealsScreen';
import WalletScreen from './components/WalletScreen';
import PlanScreen from './components/PlanScreen';
import './components/ComposerDock.css';

import RequireRole from './components/RequireRole';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminUsers from './components/admin/AdminUsers';
import AdminUserDetail from './components/admin/AdminUserDetail';
import AdminStores from './components/admin/AdminStores';
import AdminMerchants from './components/admin/AdminMerchants';
import AdminTaskModeration from './components/admin/AdminTaskModeration';
import AdminCoupons from './components/admin/AdminCoupons';
import AdminFlyers from './components/admin/AdminFlyers';
import AdminBroadcast from './components/admin/AdminBroadcast';
import AdminApplications from './components/admin/AdminApplications';
import AdminApplicationDetail from './components/admin/AdminApplicationDetail';
import MerchantLayout from './components/merchant/MerchantLayout';
import MerchantDashboard from './components/merchant/MerchantDashboard';
import MerchantCoupons from './components/merchant/MerchantCoupons';
import MerchantFlyers from './components/merchant/MerchantFlyers';
import MerchantSubscription from './components/merchant/MerchantSubscription';
import BusinessLanding from './components/business/BusinessLanding';
import MerchantSignup from './components/business/MerchantSignup';
import MerchantPending from './components/business/MerchantPending';

import { fetchDayDate, fetchTasks, addTask, deleteTask } from './services/taskService';
import { useAuth } from './contexts/AuthContext';

const fullToShortDayMap = {
  Sunday: 'Sun',
  Monday: 'Mon',
  Tuesday: 'Tues',
  Wednesday: 'Wed',
  Thursday: 'Thur',
  Friday: 'Fri',
  Saturday: 'Sat',
};

function App() {
  const today = new Date();

  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedWeek, setSelectedWeek] = useState(Math.ceil(today.getDate() / 7));

  const [userLocation, setUserLocation] = useState(() => {
    const saved = localStorage.getItem('userLocation');
    return saved ? JSON.parse(saved) : { type: 'current', name: 'Current Location' };
  });

  const [tasks, setTasks] = useState({});
  const [lastTaskText, setLastTaskText] = useState('');
  const [lastTaskDate, setLastTaskDate] = useState('');
  const [showCalendar, setShowCalendar] = useState(true);
  // "Ask Tudu" assistant — triggered from the Header button, the chat window is
  // rendered once at the shell level (no floating FAB).
  const [assistantOpen, setAssistantOpen] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('accessToken');
  });

  // Use full day name (Sunday, Monday, etc.) to match backend format
  const selectedDay = selectedDate.toLocaleDateString('en-US', { weekday: 'long' });

  useEffect(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1;
    const week = Math.ceil(selectedDate.getDate() / 7);

    setSelectedYear(year);
    setSelectedMonth(month);
    setSelectedWeek(week);
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate || !isAuthenticated) return;

    const loadTasks = async () => {
      try {
        const shortDay = fullToShortDayMap[selectedDay] || selectedDay;
        const data = await fetchTasks(selectedYear, selectedMonth, selectedWeek, shortDay);
        // Flatten every category bucket the backend returns (Shopping, Travel,
        // Work, Health, Money, Personal, Other). Hardcoding a subset silently
        // dropped tasks in the unlisted categories.
        const formatted = Object.values(data.tasks || {}).flat();
        const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
        setTasks((prev) => ({ ...prev, [dateStr]: formatted }));
      } catch (err) {
        console.error('❌ Error loading tasks:', err);
      }
    };

    loadTasks();
  }, [selectedDate, selectedYear, selectedMonth, selectedWeek, selectedDay, isAuthenticated]);

  const handleAddTask = async (taskText, taskDate, priority, category, options) => {
    if (!taskText || taskText.trim() === '') return;

    try {
      const response = await addTask(taskText, taskDate, priority, category, options);
      const newTask = response?.parsed_task || {
        text: taskText,
        task_text: taskText,
        priority,
        category, // Ensure category is locally set too
      };

      // Key the optimistic insert off the composer's taskDate (a real
      // YYYY-MM-DD that matches DayView's lookup key). The backend's
      // parsed_task.date is "YYYY-M-Weekday" (weekday name, unpadded month),
      // which would land the task under a key nothing renders → invisible
      // until a manual refresh.
      const backendDate = taskDate;
      // Parse "YYYY-MM-DD" in local time (a bare date string is UTC → off by a
      // day in US timezones, which would move the view off the new task's day).
      const rawDate = response?.parsed_task?.date;
      const parsedDateObj = rawDate
        ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? `${rawDate}T00:00:00` : rawDate)
        : null;

      // Optional: update calendar view if parsed date is different
      if (parsedDateObj && !isNaN(parsedDateObj)) {
        setSelectedDate(parsedDateObj);
      }

      setTasks((prev) => ({
        ...prev,
        [backendDate]: [...(prev[backendDate] || []), newTask],
      }));

      setLastTaskText(taskText);
      setLastTaskDate(backendDate);
    } catch (err) {
      console.error('❌ Error adding task:', err);
    }
  };

  const addBlankTaskToUIOnly = (dateStr) => {
    setTasks((prev) => {
      const updated = { ...prev };
      const newTask = { text: '', priority: false, isEditing: true };
      updated[dateStr] = [...(updated[dateStr] || []), newTask];
      return updated;
    });
  };

  const handleDeleteTask = async (dateStr, index) => {
    const target = tasks[dateStr]?.[index];
    if (!target) return;

    // Optimistically remove from the UI, then persist to the backend so the
    // task doesn't reappear on the next refresh.
    setTasks((prev) => {
      const updated = { ...prev };
      if (updated[dateStr]) {
        updated[dateStr] = updated[dateStr].filter((_, i) => i !== index);
        if (updated[dateStr].length === 0) delete updated[dateStr];
      }
      return updated;
    });

    try {
      await deleteTask(target);
    } catch (err) {
      console.error('❌ Error deleting task on server:', err);
      // Restore the row at its original position if the server delete failed.
      setTasks((prev) => {
        const arr = [...(prev[dateStr] || [])];
        arr.splice(index, 0, target);
        return { ...prev, [dateStr]: arr };
      });
    }
  };

  const handleTogglePriority = (dateStr, index) => {
    setTasks((prev) => {
      const updated = { ...prev };
      if (updated[dateStr]?.[index]) {
        updated[dateStr][index] = {
          ...updated[dateStr][index],
          priority: !updated[dateStr][index].priority,
        };
      }
      return updated;
    });
  };

  const handleMapClick = (taskText, taskDate) => {
    console.log(`🗺️ Show map for ${taskText} on ${taskDate}`);
  };

  const handleSuggestClick = (taskText, taskDate) => {
    setLastTaskText(taskText);
    setLastTaskDate(taskDate);
    setAssistantOpen(true);
  };

  const handleWeekTabClick = (shortDay) => {
    const todayClone = new Date(selectedDate);
    const targetIndex = Object.values(fullToShortDayMap).indexOf(shortDay);
    const diff = (targetIndex - todayClone.getDay() + 7) % 7;
    const nextDay = new Date(todayClone);
    nextDay.setDate(todayClone.getDate() + diff);
    setSelectedDate(nextDay);
  };

  const isValidDate = selectedDate instanceof Date && !isNaN(selectedDate.getTime());

  // Wrapped in a child component so we can call useLocation() (needs Router
  // context) and conditionally hide the legacy Header on portal routes.
  const AppShell = () => {
    const { pathname } = useLocation();
    const inPortal = pathname.startsWith('/admin')
      || pathname.startsWith('/merchant')
      || pathname.startsWith('/business');

    // Primary section nav (Tasks/Daily/Team/Deals/Wallet/Me) shows on the
    // authenticated app sections — not on portals or public marketing pages.
    const appSectionPaths = ['/dashboard', '/routines', '/shared', '/plan', '/deals', '/wallet', '/profile', '/settings', '/activity'];
    const showAppNav = isAuthenticated && !inPortal
      && appSectionPaths.some((p) => pathname.startsWith(p));

    return (
      <>
        {/* Header + section nav share one sticky top bar. Sticky (not fixed) so
            it stays pinned even inside a transformed ancestor — e.g. a
            responsive-preview wrapper — where position:fixed would scroll away.
            On the landing route it overlays the hero (fixed) instead. */}
        {!inPortal && (
          <div className={`app-topbar${pathname === '/' ? ' app-topbar-overlay' : ''}`}>
            <Header
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
              selectedWeek={selectedWeek}
              setSelectedWeek={setSelectedWeek}
              showCalendar={showCalendar}
              setShowCalendar={setShowCalendar}
              isAuthenticated={isAuthenticated}
              setIsAuthenticated={setIsAuthenticated}
              userLocation={userLocation}
              setUserLocation={setUserLocation}
              onAskTudu={() => setAssistantOpen(true)}
            />
            {showAppNav && <AppNav />}
          </div>
        )}

        {/* "Ask Tudu" assistant chat window — opened from the Header button.
            Rendered once here so it's available across the app sections; lifted
            above the composer bar only on the dashboard. */}
        {isAuthenticated && !inPortal && (
          <SuggestedPlan
            taskText={lastTaskText}
            taskDate={lastTaskDate}
            open={assistantOpen}
            onClose={() => setAssistantOpen(false)}
            liftAboveComposer={pathname.startsWith('/dashboard')}
          />
        )}

        <Routes>
          <Route path="/" element={isAuthenticated ? <RoleHomeRedirect /> : <LandingPage />} />
          <Route path="/login" element={<LoginPage setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/signup" element={<SignupPage setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/about" element={<AboutUsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/profile" element={isAuthenticated ? <ProfilePage /> : <Navigate to="/login" replace />} />
          <Route path="/settings" element={isAuthenticated ? <SettingsPage /> : <Navigate to="/login" replace />} />
          <Route path="/activity" element={isAuthenticated ? <ActivityPage /> : <Navigate to="/login" replace />} />
          <Route path="/help" element={<HelpPage />} />
          {/* Public, unauthenticated — App Store and Play both require this URL
              to be reachable without signing in. */}
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Mobile-parity sections being ported to web (see plan M2–M3).
              /shared is backed by the existing groups UI as a first cut;
              /routines, /deals, /wallet are stubs until their milestone. */}
          <Route
            path="/shared"
            element={isAuthenticated ? <SharedTabView selectedDate={selectedDate} /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/routines"
            element={isAuthenticated ? <RoutinesScreen /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/plan"
            element={isAuthenticated ? <PlanScreen /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/deals"
            element={isAuthenticated ? <DealsScreen /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/wallet"
            element={isAuthenticated ? <WalletScreen /> : <Navigate to="/login" replace />}
          />

          {/* TUDU Business — public marketing + merchant signup + pending screen */}
          <Route path="/business" element={<BusinessLanding />} />
          <Route path="/business/signup" element={<MerchantSignup />} />
          <Route
            path="/business/pending"
            element={
              <RequireRole roles={['pending_merchant', 'merchant', 'admin']}>
                <MerchantPending />
              </RequireRole>
            }
          />

          {/* Admin panel — gated by RequireRole, lives under AdminLayout */}
          <Route
            path="/admin"
            element={<RequireRole roles="admin"><AdminLayout /></RequireRole>}
          >
            <Route index element={<AdminDashboard />} />
            <Route path="applications" element={<AdminApplications />} />
            <Route path="applications/:id" element={<AdminApplicationDetail />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="stores" element={<AdminStores />} />
            <Route path="merchants" element={<AdminMerchants />} />
            <Route path="moderation" element={<AdminTaskModeration />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="flyers" element={<AdminFlyers />} />
            <Route path="broadcast" element={<AdminBroadcast />} />
          </Route>

          {/* Merchant portal — gated by RequireRole, lives under MerchantLayout */}
          <Route
            path="/merchant"
            element={<RequireRole roles={['merchant', 'admin']}><MerchantLayout /></RequireRole>}
          >
            <Route index element={<MerchantDashboard />} />
            <Route path="coupons" element={<MerchantCoupons />} />
            <Route path="flyers" element={<MerchantFlyers />} />
            <Route path="subscription" element={<MerchantSubscription />} />
          </Route>

          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                isValidDate ? (
                  <div className="App dashboard-floating">
                    {/* The "Ask Tudu" assistant now lives in the Header and is
                        rendered once at the shell level (see AppShell). */}
                    <div className="dashboard-content">
                      {/* Main Column (Full Center) */}
                      <div className="dashboard-main-col">
                        <div className="task-panel">

                          <DayView
                            selectedDate={selectedDate}
                            tasks={tasks}
                            onDeleteTask={handleDeleteTask}
                            onTogglePriority={handleTogglePriority}
                            onMapClick={handleMapClick}
                            onSuggestClick={handleSuggestClick}
                            onAddTask={handleAddTask}
                            onAddBlankTask={addBlankTaskToUIOnly}
                            userLocation={userLocation}
                          />
                        </div>
                      </div>
                    </div>

                    {/* ChatGPT-style floating composer pinned to the bottom */}
                    <div className="composer-dock">
                      <div className="composer-dock-inner">
                        <TaskComposer selectedDate={selectedDate} onAddTask={handleAddTask} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="tudu-loader-screen">
                    <div className="tudu-loader-wrap">
                      <TuduLoader />
                    </div>
                    <div className="tudu-loader-caption">Loading calendar…</div>
                  </div>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </>
    );
  };

  return (
    <Router>
      <AppShell />
    </Router>
  );
}

// Lands an authenticated user on the right home page for their role.
// Admins → /admin, merchants → /merchant, pending merchants → /business/pending,
// everyone else → /dashboard.
function RoleHomeRedirect() {
  const { loading, user } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading…</div>;
  const role = user?.is_staff || user?.is_superuser ? 'admin' : user?.role || 'customer';
  if (role === 'admin') return <Navigate to="/admin" replace />;
  if (role === 'merchant') return <Navigate to="/merchant" replace />;
  if (role === 'pending_merchant') return <Navigate to="/business/pending" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default App;
